import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { mobileSchema, MobileFormValues } from '../rules/mobileSchema'
import { DateOfBirthField } from '@/onboarding/shared/components/DateOfBirthField'
import { MobileNumberField } from '@/onboarding/shared/components/MobileNumberField'
import { ONBOARDING_COPY } from '@/locales/en'

export type PageProps = {
    progress?: number
    defaultValues?: MobileFormValues
    errors?: Partial<Record<string, string>>
    loading?: boolean
    saving?: boolean
    disabled?: boolean
}

interface ViewModelProps extends PageProps {
    /** Form submission handler — *required for production use*; may be omitted in Storybook previews where only form state is displayed. */
    onSubmit?: (data: MobileFormValues) => void
    onBack?: () => void
}

export function MobileForm({
    progress = 25,
    defaultValues,
    errors: externalErrors,
    loading,
    saving,
    disabled,
    onSubmit,
    onBack,
}: ViewModelProps) {
    const {
        register,
        handleSubmit,
        control,
        trigger,
        setValue,
        watch,
        reset,
        setFocus,
        formState: { errors: formErrors, isValid },
    } = useForm<MobileFormValues>({
        resolver: yupResolver(mobileSchema),
        mode: 'onChange',
        defaultValues: {
            countryCode: '+61',
            ...defaultValues,
        },
    })

    useEffect(() => {
        if (defaultValues) {
            reset({
                countryCode: defaultValues.countryCode || '+61',
                mobile: defaultValues.mobile || '',
                dateOfBirth: defaultValues.dateOfBirth || '',
            })
        }
    }, [defaultValues, reset])

    useEffect(() => {
        setFocus('mobile')
    }, [setFocus])

    const countryCode = watch('countryCode', '+61')
    const mobile = watch('mobile', '')

    const displayErrors: PageProps['errors'] = externalErrors

    const isDisabled = disabled ?? (!isValid || loading || saving)

    return (
        <Onboarding
            title={ONBOARDING_COPY.aboutYou.title}
            isLoading={saving || loading}
            description={ONBOARDING_COPY.aboutYou.mobileDescription}
            cta={ONBOARDING_COPY.next}
            onSubmit={onSubmit ? handleSubmit(onSubmit) : () => {}}
            onBack={onBack}
            disabled={isDisabled}
            progress={progress}
        >
            <div className="flex flex-col w-full gap-6">
                <MobileNumberField
                    control={control}
                    register={register}
                    setValue={setValue}
                    trigger={trigger}
                    countryCode={countryCode}
                    mobile={mobile}
                    mobileError={displayErrors?.mobile}
                    countryCodeError={displayErrors?.countryCode}
                    formErrors={formErrors}
                />
                <DateOfBirthField control={control} formErrors={formErrors} />
            </div>
        </Onboarding>
    )
}
